import { useStore } from '@tanstack/react-store';
import * as React from 'react';
import { cn } from '~/lib/utils';
import { formatDate, formatDayOfWeek, langStore } from '~/store/langStore';

interface DayButtonProps {
  date: string;
  isSelected: boolean;
  onClick: () => void;
}

export function DayButton({ date, isSelected, onClick }: DayButtonProps) {
  return (
    <button
      type="button"
      key={date}
      onClick={onClick}
      className={cn(
        'rounded-full px-2 py-1 text-xs font-medium transition-all duration-200 sm:text-base md:px-4 md:py-2',
        isSelected
          ? 'bg-red shadow-red/20 text-white shadow-lg'
          : 'text-red bg-white/5 hover:text-red/80 hover:bg-white/10',
      )}
    >
      {formatDayOfWeek(date)}
    </button>
  );
}

interface Programme {
  [key: string]: any;
}

interface DayProgrammes {
  date: string;
  dayNumber: number;
  programmes: Programme[];
}

interface ProgrammeListProps {
  dailyData: DayProgrammes[];
  emptyMessage: {
    HU: string;
    EN: string;
    RS: string;
  };
  showAge?: boolean;
}

export function ProgrammeList({ dailyData, emptyMessage, showAge = false }: ProgrammeListProps) {
  const locale = useStore(langStore, s => s);
  const [selectedDay, setSelectedDay] = React.useState<string>('');

  React.useEffect(() => {
    // Set default selected day to the first day
    if (dailyData.length > 0 && !selectedDay) {
      setSelectedDay(dailyData[0].date);
    }
  }, [dailyData, selectedDay]);

  const getLocalizedField = (programme: Programme, field: string) => {
    // First try the current locale
    const currentLocaleField = `${field}-${locale}`;
    if (programme[currentLocaleField] && programme[currentLocaleField].trim() !== '') {
      return programme[currentLocaleField];
    }

    // If current locale is not available, try other locales in order of preference
    const locales = ['HU', 'EN', 'RS'];
    for (const lang of locales) {
      const langField = `${field}-${lang}`;
      if (programme[langField] && programme[langField].trim() !== '') {
        return programme[langField];
      }
    }

    // Fallback to the base field if no localized version exists
    return programme[field] || '';
  };

  const getAvailableLanguages = (programme: Programme) => {
    const languages: string[] = [];
    const locales = ['HU', 'EN', 'RS'];

    locales.forEach((lang) => {
      const nameField = `Name-${lang}`;
      if (programme[nameField] && programme[nameField].trim() !== '') {
        languages.push(lang);
      }
    });

    return languages;
  };

  if (dailyData.length === 0) {
    return (
      <div className="py-12 text-center">
        <h1 className="mb-4 text-2xl font-bold">
          {emptyMessage[locale as keyof typeof emptyMessage]}
        </h1>
      </div>
    );
  }

  return (
    <>
      {/* Day tabs */}
      <div className="mb-4 flex flex-wrap justify-center gap-2 md:mb-8">

        {dailyData.map(day => (
          <DayButton
            key={day.date}
            date={day.date}
            isSelected={selectedDay === day.date}
            onClick={() => setSelectedDay(day.date)}
          />
        ))}
      </div>

      {/* Programmes for selected day */}
      {dailyData.map(day => (
        <div
          key={day.date}
          className={cn(
            'space-y-6',
            selectedDay === day.date ? 'block' : 'hidden',
          )}
        >
          <div className="mb-3 text-center md:mb-6">
            <h1 className="text-lg font-bold text-black md:text-2xl">
              {formatDate(day.date)}
            </h1>
          </div>

          {/* Programmes */}
          <div className="grid gap-4">
            {day.programmes.map((programme, index) => (
              <div
                key={`${programme.id || index}`}
                className="rounded-lg bg-white/5 p-4 transition-colors duration-200 hover:bg-white/10"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className={cn(
                          'mb-1 text-left text-sm font-semibold md:text-base',
                          showAge ? 'text-black' : '',
                        )}
                        >
                          {getLocalizedField(programme, 'Name')}
                        </h3>
                        {getAvailableLanguages(programme).length > 0 && (
                          <p className="mb-2 text-sm text-gray-500">
                            🌐
                            {' '}
                            {getAvailableLanguages(programme).join(', ')}
                          </p>
                        )}
                        {getLocalizedField(programme, 'Description') && (
                          <p className="mb-2 text-sm text-gray-300">
                            {getLocalizedField(programme, 'Description')}
                          </p>
                        )}
                        {programme.Place && (
                          <p className="text-red text-sm">
                            📍
                            {' '}
                            {programme.Place}
                          </p>
                        )}
                        {showAge && programme.Age && (
                          <p className="text-sm text-gray-400">
                            👶
                            {' '}
                            {programme.Age}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-red text-lg font-bold">
                        {programme.Time}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="h-12 md:hidden" />
        </div>

      ))}
    </>
  );
}
